// Every assigned canvas area, under the Today tab's people list (Oct 19, 2026)
// and on the Areas tab (Oct 23, 2026).
//
// Greg: "list all assigned areas below so they can add new team members to
// them and look at all the stats and kpi's for that area."
//
// One card per area (all reps sharing a boundary collapse to one card). The
// collapsed card answers "where, how big, who, how worked"; "Stats" opens the
// full KPI set — activity by window, the pin breakdown, per-person numbers,
// deals that came out of the area since it was assigned — and "Map" opens the
// same satellite map + hail list the audit board's Team View uses.
//
// Daily areas (Oct 23): today's areas come first with a Today pill and the
// stop they were drawn around; an older daily area still open says so and
// can be carried to today; a crew manager can Expire a daily area or Remove a
// standing one from the card (the same write as the Team View's Remove).
//
// Crew managers can add / remove people and rename an area; everyone who can
// open the board can read it. Numbers are live (canvas_area_stats), not tied
// to the day picker above.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../../lib/supabase";
import {
  addAreaMember, areaMapHref, areaName, areaPlace, carryArea, coverage, doorsLeft, expireArea,
  firstName, fmtSince, relTime, removeAreaMember, renameArea, type AreaStats,
} from "../../../lib/canvasAreas";
import { KIND_LABEL, areaDayState, sortAreasForDay } from "../../../lib/dailyAreas";
import { dayLabel, todayET } from "../../../lib/dailyCommitments";
import AreaMapStormDrawer from "./AreaMapStormDrawer";
import { CoverageMeter, HailChip, KnockSparkline, PinStateBar, PinStateLegend, Stat } from "./AreaBits";

interface Person { id: string; full_name: string; role: string }

const ROLE_SHORT: Record<string, string> = { asm: "ASM", team_lead: "TL", restore_manager: "RM", canvasser: "CNV", roof_specialist: "RS" };

function fmtMoney(n: number): string {
  if (!n) return "$0";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}k`;
  return `$${Math.round(n)}`;
}

function pct(num: number, den: number): string {
  return den > 0 ? `${Math.round((num / den) * 100)}%` : "—";
}

export default function AreaBoard({ areas, error, people, canWrite, onChanged, onRetry, day, filter = "all", title }: {
  areas: AreaStats[] | null;
  error: string | null;
  people: Person[];
  canWrite: boolean;
  onChanged: () => void;
  onRetry: () => void;
  /** The day on screen (YYYY-MM-DD); today when omitted. Decides which areas count as today's. */
  day?: string;
  /** all = every area (today's first); daily = dated areas only; standing = undated only. */
  filter?: "all" | "daily" | "standing";
  title?: string;
}) {
  const onDay = day ?? todayET();
  // Boundary polygons for the map drawer, fetched the first time a map opens
  // (same RPC the canvas map and the Team View use).
  const [geom, setGeom] = useState<Map<string, unknown> | null>(null);
  const [wantGeom, setWantGeom] = useState(false);
  useEffect(() => {
    if (!wantGeom) return;
    let cancelled = false;
    (async () => {
      const { data, error: e } = await supabase.rpc("get_active_canvas_areas_geojson");
      if (cancelled || e) return;
      const m = new Map<string, unknown>();
      for (const f of ((data as { features?: Array<{ geometry?: unknown; properties?: { area_group_id?: string } }> })?.features ?? [])) {
        const gid = f?.properties?.area_group_id;
        if (gid && f.geometry && !m.has(gid)) m.set(gid, f.geometry);
      }
      setGeom(m);
    })();
    return () => { cancelled = true; };
  }, [wantGeom, areas]);

  const shown = useMemo(() => {
    const xs = (areas ?? []).filter((a) => filter === "all" ? true : filter === "daily" ? !!a.for_date : !a.for_date);
    return sortAreasForDay(xs, onDay);
  }, [areas, filter, onDay]);

  const totals = useMemo(() => ({
    pins: shown.reduce((s, a) => s + a.pin_count, 0),
    doors: shown.reduce((s, a) => s + doorsLeft(a.states), 0),
    people: new Set(shown.flatMap((a) => a.reps.map((r) => r.user_id))).size,
    knocks7: shown.reduce((s, a) => s + a.activity.knocks_7d, 0),
  }), [shown]);

  const emptyText = filter === "daily"
    ? `No daily area for ${onDay === todayET() ? "today" : dayLabel(onDay)} yet.${canWrite ? " Tap Area here on a person's stop, or draw one on the canvas map." : ""}`
    : filter === "standing"
    ? `No standing areas right now.${canWrite ? " Draw one on the canvas map — lasso the streets, then pick the people." : ""}`
    : `No areas are assigned right now.${canWrite ? " Draw one on the canvas map — lasso the streets, then pick the people." : ""}`;

  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
        <div>
          <div className="text-sm font-bold text-slate-900">{title ?? "📐 Assigned areas"}</div>
          <div className="text-[11px] text-slate-500">
            {areas
              ? `${shown.length} ${shown.length === 1 ? "area" : "areas"} · ${totals.pins.toLocaleString()} pins · ${totals.doors.toLocaleString()} doors left · ${totals.people} people · ${totals.knocks7} knocks in 7 days · live`
              : "Live — every area anyone is assigned to right now"}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canWrite && (
            <a href="/canvas?assign=1" target="_blank" rel="noopener"
              className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-bold text-white">
              ✏️ Draw a new area ↗
            </a>
          )}
          <Link to="/canvas/manager-audit?mode=areas&view=review" className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50"
            title="Areas tab: how the areas did this week and this month">
            Week & month review →
          </Link>
          <Link to="/canvas/manager-audit?mode=team" className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50"
            title="Team View: mark areas reviewed, remove an area, see removed areas">
            Team View table →
          </Link>
        </div>
      </div>

      {error && !areas ? (
        <div className="p-3 text-sm text-rose-700">
          Couldn't load the areas: {error}{" "}
          <button className="ml-1 font-semibold underline" onClick={onRetry}>Retry</button>
        </div>
      ) : !areas ? (
        <div className="space-y-2 p-3">
          {[0, 1].map((i) => <div key={i} className="h-24 animate-pulse rounded-lg bg-slate-100" />)}
        </div>
      ) : shown.length === 0 ? (
        <div className="p-6 text-center text-sm text-slate-500">{emptyText}</div>
      ) : (
        <div className="space-y-3 p-3">
          {shown.map((a) => (
            <AreaCard key={a.area_group_id} area={a} people={people} canWrite={canWrite} day={onDay}
              onChanged={onChanged} geometry={geom?.get(a.area_group_id) ?? null}
              geomLoaded={!!geom} onWantGeom={() => setWantGeom(true)} />
          ))}
        </div>
      )}
    </div>
  );
}

function AreaCard({ area, people, canWrite, day, onChanged, geometry, geomLoaded, onWantGeom }: {
  area: AreaStats; people: Person[]; canWrite: boolean; day: string; onChanged: () => void;
  geometry: unknown | null; geomLoaded: boolean; onWantGeom: () => void;
}) {
  const [statsOpen, setStatsOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [picking, setPicking] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [label, setLabel] = useState(area.area_label ?? "");
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const name = areaName(area);
  const place = areaPlace(area);
  const act = area.activity;
  const state = areaDayState(area, day);
  // "Knocks today" only makes sense for a daily area while the board shows real today.
  const showToday = !!area.for_date && day === todayET();
  const onIds = useMemo(() => new Set(area.reps.map((r) => r.user_id)), [area.reps]);
  const candidates = people.filter((p) => !onIds.has(p.id));

  async function add(p: Person) {
    setBusy(p.id); setMsg(null);
    try {
      const r = await addAreaMember(area.area_group_id, p.id);
      setMsg(r.already ? `${firstName(p.full_name)} was already on it.` : `${p.full_name} added.`);
      onChanged();
    } catch (e) { setMsg((e as Error).message); } finally { setBusy(null); }
  }

  async function remove(userId: string, fullName: string) {
    const alone = area.reps.length <= 1;
    const ok = window.confirm(alone
      ? `${fullName} is the only one on ${name}. Taking them off ends the area — its pins go back to the open pool. Continue?`
      : `Take ${fullName} off ${name}?`);
    if (!ok) return;
    setBusy(userId); setMsg(null);
    try {
      const r = await removeAreaMember(area.area_group_id, userId);
      setMsg(r.areaStillActive ? `${fullName} is off ${name}.` : `${name} ended — nobody is on it now.`);
      onChanged();
    } catch (e) { setMsg((e as Error).message); } finally { setBusy(null); }
  }

  async function saveName() {
    setBusy("rename"); setMsg(null);
    try {
      await renameArea(area.area_group_id, label);
      setRenaming(false);
      onChanged();
    } catch (e) { setMsg((e as Error).message); } finally { setBusy(null); }
  }

  async function end() {
    const verb = area.for_date ? "Expire" : "Remove";
    if (!window.confirm(`${verb} ${name}? It comes off everyone's map and its pins go back to the open pool.`)) return;
    setBusy("end"); setMsg(null);
    try {
      await expireArea(area.area_group_id);
      setMsg(`${name} ${area.for_date ? "expired" : "removed"}.`);
      onChanged();
    } catch (e) { setMsg((e as Error).message); } finally { setBusy(null); }
  }

  async function carry() {
    setBusy("carry"); setMsg(null);
    try {
      const r = await carryArea(area.area_group_id, day);
      setMsg(`${name} carried to ${dayLabel(day, { weekday: "long" })} for ${r.reps} ${r.reps === 1 ? "person" : "people"}.`);
      onChanged();
    } catch (e) { setMsg((e as Error).message); } finally { setBusy(null); }
  }

  const pill =
    state === "today" ? <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold text-orange-800">Today</span>
    : state === "past" ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">Still open from {dayLabel(area.for_date!)}</span>
    : state === "future" ? <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">For {dayLabel(area.for_date!)}</span>
    : null;

  return (
    <div className={`rounded-lg border p-3 ${state === "today" ? "border-orange-200" : state === "past" ? "border-amber-200" : "border-slate-200"}`}>
      {/* Name + where */}
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          {renaming ? (
            <div className="flex flex-wrap items-center gap-2">
              <input autoFocus value={label} onChange={(e) => setLabel(e.target.value.slice(0, 80))}
                onKeyDown={(e) => { if (e.key === "Enter") void saveName(); if (e.key === "Escape") setRenaming(false); }}
                placeholder={place || "Area name"}
                className="min-w-[200px] flex-1 rounded-md border border-slate-300 px-2 py-1 text-sm" />
              <button onClick={() => void saveName()} disabled={busy === "rename"}
                className="rounded-md bg-slate-900 px-3 py-1 text-xs font-bold text-white disabled:opacity-50">
                {busy === "rename" ? "Saving…" : "Save"}
              </button>
              <button onClick={() => { setRenaming(false); setLabel(area.area_label ?? ""); }}
                className="rounded-md px-2 py-1 text-xs font-semibold text-slate-500">Cancel</button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <div className="truncate text-sm font-extrabold text-slate-900">{name}</div>
              {pill}
              {canWrite && (
                <button onClick={() => { setLabel(area.area_label ?? ""); setRenaming(true); }}
                  className="rounded px-1 text-xs text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                  title={area.area_label ? "Rename this area" : "Name this area (it shows by city and ZIP until you do)"}>
                  ✎
                </button>
              )}
            </div>
          )}
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-slate-500">
            {place && name !== place && <span>📍 {place}</span>}
            <span>{area.sq_mi} sq mi</span>
            <span>{area.for_date ? `drawn ${fmtSince(area.first_assigned_at)}` : `since ${fmtSince(area.first_assigned_at)}`}</span>
            {area.storm_tied
              ? <span className="rounded bg-orange-50 px-1 font-semibold text-orange-700">⛈ Storm-tied</span>
              : area.for_date
              ? <span className="rounded bg-sky-50 px-1 font-semibold text-sky-700">Daily</span>
              : <span className="rounded bg-slate-100 px-1 font-semibold text-slate-600">Standing</span>}
            {area.notes && <span title={area.notes}>📝 {area.notes.length > 60 ? `${area.notes.slice(0, 60)}…` : area.notes}</span>}
          </div>
          {area.anchor && (area.anchor.street || area.anchor.city) && (
            <div className="mt-0.5 text-[11px] text-slate-600">
              around {[area.anchor.street, area.anchor.city].filter(Boolean).join(", ")}
              {area.anchor_kind ? ` · ${KIND_LABEL[area.anchor_kind].toLowerCase()}${area.anchor.when ? ` ${area.anchor.when}` : ""}` : ""}
            </div>
          )}
        </div>
        <HailChip hail={area.hail} />
      </div>

      {/* Who */}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {area.reps.map((r) => (
          <span key={r.user_id} className="inline-flex items-center gap-1 rounded-full bg-orange-100 py-0.5 pl-2 pr-1 text-[11px] font-semibold text-orange-900">
            {r.full_name}
            <span className="text-[9px] font-bold text-orange-700/70">{ROLE_SHORT[r.role ?? ""] ?? ""}</span>
            {canWrite ? (
              <button onClick={() => void remove(r.user_id, r.full_name)} disabled={busy === r.user_id}
                className="ml-0.5 rounded-full px-1 text-orange-700 hover:bg-orange-200 disabled:opacity-50"
                aria-label={`Take ${r.full_name} off this area`} title={`Take ${r.full_name} off this area`}>
                ×
              </button>
            ) : <span className="pr-1" />}
          </span>
        ))}
        {canWrite && (
          <button onClick={() => { setPicking((v) => !v); setMsg(null); }}
            className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${picking ? "border border-slate-300 bg-white text-slate-600" : "bg-sky-600 text-white"}`}>
            {picking ? "Done" : "＋ Add team member"}
          </button>
        )}
      </div>

      {picking && canWrite && (
        <div className="mt-2 rounded-md border border-sky-200 bg-sky-50/50 p-2">
          {candidates.length === 0 ? (
            <div className="text-[11px] text-slate-500">Everyone on the board is already on this area.</div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {candidates.map((p) => (
                <button key={p.id} onClick={() => void add(p)} disabled={busy === p.id}
                  className="rounded-full border border-slate-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-700 hover:border-sky-400 hover:bg-sky-50 disabled:opacity-50">
                  {busy === p.id ? "Adding…" : `＋ ${p.full_name}`}
                  <span className="ml-1 text-[9px] font-bold text-slate-400">{ROLE_SHORT[p.role] ?? ""}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {msg && <div className="mt-2 rounded-md bg-slate-50 px-2 py-1.5 text-xs text-slate-700 ring-1 ring-slate-200">{msg}</div>}

      {/* Headline KPIs */}
      <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-6">
        <Stat label="Pins" value={area.pin_count} hint="Knock pins inside the area right now" />
        <Stat label="Doors left" value={doorsLeft(area.states)} tone="text-orange-600" hint="Never knocked, or knocked with no answer yet" />
        <Stat label="Knocked" value={`${Math.round(coverage(area) * 100)}%`} tone="text-emerald-700"
          hint={`${act.pins_knocked_since} of ${area.pin_count} pins knocked since ${fmtSince(area.first_assigned_at)}`} />
        <Stat label={showToday ? "Knocks today" : "Knocks 7d"} value={showToday ? act.knocks_today : act.knocks_7d} />
        <Stat label={showToday ? "Talked today" : "Talked 7d"} value={showToday ? act.talked_today : act.talked_7d} tone="text-purple-700" hint="Contact made, appointment set, follow-up or renter" />
        <Stat label="Appts" value={act.appts_since} tone="text-cyan-700" hint={`Appointments set in the area since ${fmtSince(area.first_assigned_at)}`} />
      </div>
      <div className="mt-2"><PinStateBar states={area.states} total={area.pin_count} className="h-1.5" /></div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button onClick={() => setStatsOpen((v) => !v)}
          className={`rounded-md border px-2.5 py-1 text-[11px] font-bold ${statsOpen ? "border-orange-400 bg-orange-50 text-orange-700" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>
          📊 {statsOpen ? "Hide stats ▲" : "All stats ▾"}
        </button>
        <button onClick={() => { setMapOpen((v) => !v); onWantGeom(); }}
          className={`rounded-md border px-2.5 py-1 text-[11px] font-bold ${mapOpen ? "border-orange-400 bg-orange-50 text-orange-700" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>
          🗺 {mapOpen ? "Hide map ▲" : "Map + hail ▾"}
        </button>
        <a href={areaMapHref(area.area_group_id)} target="_blank" rel="noopener"
          className="rounded-md bg-orange-50 px-2.5 py-1 text-[11px] font-bold text-orange-700 hover:bg-orange-100">
          Open on canvas ↗
        </a>
        {canWrite && state === "past" && (
          <button onClick={() => void carry()} disabled={busy !== null}
            className="rounded-md bg-orange-500 px-2.5 py-1 text-[11px] font-bold text-white hover:bg-orange-600 disabled:opacity-50">
            {busy === "carry" ? "Carrying…" : `Carry to ${dayLabel(day, { weekday: "long" })}`}
          </button>
        )}
        {canWrite && (
          <button onClick={() => void end()} disabled={busy !== null}
            className="rounded-md border border-rose-200 bg-white px-2.5 py-1 text-[11px] font-bold text-rose-600 hover:bg-rose-50 disabled:opacity-50"
            title={area.for_date ? "End this daily area for everyone on it" : "End this standing area for everyone on it"}>
            {busy === "end" ? "…" : area.for_date ? "Expire" : "Remove area"}
          </button>
        )}
        <span className="ml-auto text-[11px] text-slate-400">
          Last knock {relTime(act.last_knock_at)}{act.last_knock_by_name ? ` · ${act.last_knock_by_name}` : ""}
        </span>
      </div>

      {statsOpen && <AreaStatsDetail area={area} />}

      {mapOpen && (
        <div className="mt-3">
          {geometry ? (
            <AreaMapStormDrawer geometry={geometry} />
          ) : (
            <div className="rounded-md bg-slate-50 p-3 text-xs text-slate-500">
              {geomLoaded ? "No boundary on file for this area — can't draw a map." : "Loading the map…"}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AreaStatsDetail({ area }: { area: AreaStats }) {
  const act = area.activity;
  const since = fmtSince(area.first_assigned_at);
  const windows: Array<{ label: string; k: number; t: number; a: number }> = [
    { label: "Today", k: act.knocks_today, t: act.talked_today, a: act.appts_today },
    { label: "7 days", k: act.knocks_7d, t: act.talked_7d, a: act.appts_7d },
    { label: "30 days", k: act.knocks_30d, t: act.talked_30d, a: act.appts_30d },
    { label: `Since ${since}`, k: act.knocks_since, t: act.talked_since, a: act.appts_since },
  ];
  const knocks14 = area.daily.reduce((s, d) => s + d.n, 0);

  return (
    <div className="mt-3 space-y-4 rounded-md border border-slate-200 bg-slate-50/60 p-3">
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Activity by window */}
        <div>
          <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">Activity in the area</div>
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wide text-slate-500">
              <tr className="border-b border-slate-200">
                <th className="py-1 text-left font-semibold" />
                {windows.map((w) => <th key={w.label} className="py-1 text-right font-semibold">{w.label}</th>)}
              </tr>
            </thead>
            <tbody className="tabular-nums">
              <tr className="border-b border-slate-100">
                <td className="py-1 text-slate-600">Knocks</td>
                {windows.map((w) => <td key={w.label} className="py-1 text-right font-bold text-slate-900">{w.k}</td>)}
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-1 text-slate-600">Talked</td>
                {windows.map((w) => <td key={w.label} className="py-1 text-right font-bold text-purple-700">{w.t}</td>)}
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-1 text-slate-600">Talk rate</td>
                {windows.map((w) => <td key={w.label} className="py-1 text-right text-slate-700">{pct(w.t, w.k)}</td>)}
              </tr>
              <tr>
                <td className="py-1 text-slate-600">Appts</td>
                {windows.map((w) => <td key={w.label} className="py-1 text-right font-bold text-cyan-700">{w.a}</td>)}
              </tr>
            </tbody>
          </table>
          <div className="mt-2 flex items-end gap-3">
            <KnockSparkline daily={area.daily} />
            <div className="text-[11px] text-slate-500">{knocks14} knocks in the last 14 days (everyone)</div>
          </div>
        </div>

        {/* Pins + coverage */}
        <div>
          <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
            {area.pin_count.toLocaleString()} pins inside the area now
          </div>
          <PinStateBar states={area.states} total={area.pin_count} className="h-2.5" />
          <div className="mt-2"><PinStateLegend states={area.states} hideZero={false} /></div>
          <div className="mt-3"><CoverageMeter area={area} /></div>
          <div className="mt-3 text-[11px] text-slate-600">
            <b className="text-slate-800">From this area since {since}:</b>{" "}
            {area.deals.deals} {area.deals.deals === 1 ? "deal" : "deals"} · {area.deals.claims} at claim or later ·{" "}
            {area.deals.builds} built{area.deals.builds > 0 ? ` · ${fmtMoney(area.deals.revenue)} revenue` : ""}
          </div>
          {area.hail && (
            <div className="mt-1 text-[11px] text-slate-600">
              <b className="text-slate-800">Hail within a mile, last 12 months:</b>{" "}
              largest {area.hail.max_in.toFixed(2)}″ · {area.hail.days} storm {area.hail.days === 1 ? "day" : "days"} with 1″+
            </div>
          )}
        </div>
      </div>

      {/* Per person */}
      <div>
        <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">By person</div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-xs">
            <thead className="text-[10px] uppercase tracking-wide text-slate-500">
              <tr className="border-b border-slate-200">
                <th className="py-1 text-left font-semibold">Person</th>
                <th className="py-1 text-right font-semibold">Today</th>
                <th className="py-1 text-right font-semibold">Knocks 7d</th>
                <th className="py-1 text-right font-semibold">Talked 7d</th>
                <th className="py-1 text-right font-semibold">Knocks since added</th>
                <th className="py-1 text-right font-semibold">Talked</th>
                <th className="py-1 text-right font-semibold">Appts</th>
                <th className="py-1 text-right font-semibold">Last knock</th>
              </tr>
            </thead>
            <tbody className="tabular-nums">
              {area.reps.map((r) => (
                <tr key={r.user_id} className="border-b border-slate-100">
                  <td className="py-1 font-semibold text-slate-800">
                    {r.full_name}
                    <span className="ml-1 text-[10px] font-normal text-slate-400">since {fmtSince(r.assigned_at)}</span>
                  </td>
                  <td className="py-1 text-right">{r.knocks_today}</td>
                  <td className="py-1 text-right font-bold">{r.knocks_7d}</td>
                  <td className="py-1 text-right text-purple-700">{r.talked_7d}</td>
                  <td className="py-1 text-right">{r.knocks_since}</td>
                  <td className="py-1 text-right text-purple-700">{r.talked_since}</td>
                  <td className="py-1 text-right text-cyan-700">{r.appts_since}</td>
                  <td className="py-1 text-right text-slate-500">{relTime(r.last_knock_at)}</td>
                </tr>
              ))}
              {(act.others_knocks_since > 0 || act.others_knocks_7d > 0) && (
                <tr className="text-slate-500">
                  <td className="py-1 italic">Not on this area</td>
                  <td className="py-1 text-right">—</td>
                  <td className="py-1 text-right">{act.others_knocks_7d}</td>
                  <td className="py-1 text-right">—</td>
                  <td className="py-1 text-right">{act.others_knocks_since}</td>
                  <td className="py-1 text-right">—</td>
                  <td className="py-1 text-right">—</td>
                  <td className="py-1 text-right">—</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-1 text-[10px] text-slate-400">
          Knocks = every logged visit · Talked = contact made, appointment set, follow-up or renter · same counting as the canvas day tiles.
        </div>
      </div>
    </div>
  );
}
